/**
 * AlertTileStack — the docked alert panel, split into two stacked sections:
 *
 *  ┌─ "Live camera stream" ────────────────────────────────────────────────┐
 *  │  One LIVE video tile per camera that alerted THIS session — newest /     │
 *  │  continued on top. The player is INJECTED (local ULLFUPlayer / cloud       │
 *  │  HexLivePlayer). While a tile is still CONNECTING it shows the alert's    │
 *  │  saved snapshot as a poster, so the alert is visible instantly and the    │
 *  │  live video takes over when frames flow. A fresh alert previews for       │
 *  │  PREVIEW_MS of *visible* video then auto-stops (→ onExpire, parent drops  │
 *  │  the tile); "Continue" keeps it live for EXTENDED_MS and pins it to the   │
 *  │  top (→ onContinue).                                                      │
 *  └──────────────────────────────────────────────────────────────────────────┘
 *  ┌─ "Alert preview" ──────────────────────────────────────────────────────┐
 *  │  Paginated, latest-first list of ALL active alerts as NON-streaming cards │
 *  │  (snapshot thumbnail / placeholder — no player). Infinite-scroll: a       │
 *  │  sentinel near the bottom calls onLoadMore() until hasMore is false.      │
 *  └──────────────────────────────────────────────────────────────────────────┘
 *
 * Why two sections: only the most-recent alert(s) stream — on load the single latest
 * active alert is streamed (the "primary" tile) to warm the WebRTC transport, and each
 * new arrival streams on top; older alerts show as non-streaming cards (never a wall of
 * reconnecting players). The primary tile streams with NO countdown (keeping the
 * connection warm); when a newer alert supersedes it, it moves down and its preview timer
 * starts — but ONLY once the new primary is actually streaming (a demoted tile HOLDS its
 * live stream while the newer tile is still connecting, so the operator never faces a
 * gap where the old stream died and the new one is still black). When the timer elapses
 * the stream stops but the alert lives on as a card below. "Continue" keeps a stream
 * pinned up top for EXTENDED_MS.
 *
 * HELD tiles are the exception: the board names them in `heldCameraIds` — every camera with
 * a CRITICAL alert, plus the newest non-critical one — and they get no countdown at any
 * point. A critical keeps streaming until it is acknowledged / assigned / resolved (clearing
 * it takes the alert off the board, which drops the tile the same way an expiry would); the
 * newest lesser alert keeps streaming until something newer replaces it. So neither a
 * critical event nor the alert the operator is currently reading goes black because another
 * alert landed on top of it.
 *
 * Each live tile is keyed by cameraId (stable): a camera that re-alerts moves to
 * the top without remounting, so its player/timer is preserved.

 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { BoltIcon, PhotoIcon, VideoCameraIcon } from '@heroicons/react/24/solid';
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  FilmIcon,
} from '@heroicons/react/24/outline';
import Tooltip from '../common/Tooltip';
import type { HexAlert, HexCounter } from '../../api/contracts';
import type { HexPlayerComponent } from './types';
import AlertCard from './AlertCard';

/** Auto-stop a demoted (second-most-recent) alert's preview after this much *visible*
 *  video — i.e. how long the previous live camera keeps streaming once a newer alert
 *  takes the primary slot, before it closes down to a snapshot card. */
const PREVIEW_MS = 60_000;
/** How long a "Continue"-d stream keeps running before it auto-stops. */
const EXTENDED_MS = 30 * 60_000;
/** If the stream never starts within this long, remove the tile — a camera that
 *  can't connect within a minute isn't worth a preview slot (the card remains below). */
const CONNECT_TIMEOUT_MS = 60_000;

/** Why a tile is exempt from the closing countdown, as the chip the operator reads. The
 *  board decides WHICH tiles are held (`heldCameraIds` — every critical, plus the newest
 *  non-critical); the severity of the alert on the tile is what distinguishes the two, and
 *  they end differently, so they must not read the same:
 *
 *    • a critical stays live until somebody actions it — no amount of waiting closes it;
 *    • the newest lesser alert stays live until a newer one takes its place.
 *
 *  Saying only "held" left the operator unable to tell which of those they were looking at. */
function heldLabel(alert: HexAlert): { chip: string; title: string } {
  return alert.severity === 'critical'
    ? {
        chip: 'Critical · live until actioned',
        title:
          'Critical alert — this stream stays open until the alert is acknowledged, assigned or resolved. It is never closed by the preview timer.',
      }
    : {
        chip: 'Newest alert · live',
        title:
          'Most recent non-critical alert — this stream stays open until a newer alert replaces it or this one is actioned.',
      };
}

type StreamPhase = 'connecting' | 'live' | 'preview' | 'extended';

/**
 * Severity, as a hairline border and a text colour — NOT as a filled surface.
 *
 * The tile used to carry the alert colour three times over: a tinted card body, a bar down each
 * edge, and a saturated footer band. Stacked down a panel that is itself mostly alert tiles,
 * that made a queue of solid red and amber blocks in which no single alert stood out — the
 * colour stopped being a signal and became the wallpaper. One light border and the alert's own
 * name in its severity colour say the same thing once, and leave the video as the brightest
 * thing on the tile, which is what the operator is here to look at.
 *
 * `ring` is separate and stays saturated: it is the arrival flash and the live halo, both of
 * which are motion rather than a standing surface.
 */
const SEV: Record<string, { border: string; text: string; ring: string; badge: string }> = {
  critical: { border: 'border-red-500/50', text: 'text-red-600 dark:text-red-400', ring: 'ring-red-500/40', badge: 'bg-red-600 text-white' },
  warning: { border: 'border-amber-400/60', text: 'text-amber-600 dark:text-amber-400', ring: 'ring-amber-400/40', badge: 'bg-amber-500 text-amber-950' },
  normal: { border: 'border-blue-500/50', text: 'text-blue-600 dark:text-blue-400', ring: 'ring-blue-500/40', badge: 'bg-blue-600 text-white' },
};

function fmtClock(ts?: number): string {
  if (!ts) return '';
  const ms = ts < 1e12 ? ts * 1000 : ts;
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function timeAgo(ts?: number): string {
  if (!ts) return '';
  const ms = ts < 1e12 ? ts * 1000 : ts;
  const diff = Math.max(0, Date.now() - ms);
  const s = Math.floor(diff / 1000);
  if (s < 10) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/** Remaining time as "9s" (< 1 min) or "m:ss". */
function fmtRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export interface AlertTile {
  cameraId: string;
  name: string;
  alert: HexAlert;
}

interface AlertTileStackProps {
  /** Live-stream tiles (cameras that alerted this session) — continued/newest first. */
  liveTiles: AlertTile[];
  /** The most-recent (primary) live tile's cameraId. It streams persistently with NO
   *  auto-stop so its subscription keeps the WebRTC transport warm; a later alert then
   *  renegotiates instantly. When superseded it moves down and begins its closing timer. */
  persistentCameraId?: string | null;
  /** Cameras whose stream must NOT be closed by a timer — the board's `heldCameraIds`
   *  (every camera with a critical alert, plus the newest non-critical one). Omitted →
   *  every tile but the primary winds down on PREVIEW_MS, as it did before. */
  heldCameraIds?: Set<string>;
  /** Paginated active alerts shown as non-streaming cards (latest-first). */
  cards: HexAlert[];
  /** cameraId → display name (for the cards). */
  nameById: Record<string, string>;
  /**
   * cameraId → outstanding alert counts, as the board already keeps them for the hex colours.
   * A live tile shows the LATEST alert on its camera; this is how it says there are others.
   */
  counters?: Record<string, HexCounter>;
  PlayerComponent: HexPlayerComponent;
  onSelect?: (cameraId: string) => void;
  /** A live tile's window elapsed without "Continue" — parent removes that camera's tile. */
  onExpire?: (cameraId: string) => void;
  /** Operator pressed "Continue" on a live tile — parent pins it (keeps streaming). */
  onContinue?: (cameraId: string) => void;
  onAcknowledge?: (alert: HexAlert) => void;
  onResolve?: (alert: HexAlert) => void;
  onViewAlert?: (alert: HexAlert) => void;
  /** Jump to a camera's recorded footage from a live tile. Omit when the caller lacks
   *  `recordings.view` — that is what hides the button. */
  onOpenRecordings?: (cameraId: string) => void;
  /** Lazy preview resolver for the non-streaming cards (cloud blob-fetches the saved
   *  snapshot through the tunnel). Forwarded to each AlertCard. */
  loadPreview?: (alert: HexAlert) => Promise<string | null>;
  /** Infinite scroll for the cards list. */
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}

interface AlertTileCardProps {
  tile: AlertTile;
  /**
   * Active alerts outstanding on this camera, from the board's counters rather than from the
   * card list — the cards are a paginated slice, so counting them would report "how many have
   * been scrolled into view", which drifts upward as the operator scrolls.
   */
  alertCount: number;
  /** True while this is the most-recent tile: stream without a countdown (keeps the
   *  connection warm). Flips false when a newer alert supersedes it → start closing. */
  persistent: boolean;
  /** True once the CURRENT primary tile is actually streaming. A demoted tile only starts
   *  its closing countdown when this is true — while the newer tile is still connecting it
   *  keeps its own live stream running so the operator never watches two black boxes. */
  demoteArmed: boolean;
  /** This tile is exempt from every closing countdown (critical, or the newest lesser
   *  alert). It stays live until its alert leaves the board. */
  held: boolean;
  /** Reports whether this tile's video is on screen (any phase past 'connecting'). The
   *  parent tracks these to know when the primary is streaming → arms demotion. */
  onStreamingChange?: (cameraId: string, streaming: boolean) => void;
  PlayerComponent: HexPlayerComponent;
  onSelect?: (cameraId: string) => void;
  onExpire?: (cameraId: string) => void;
  onContinue?: (cameraId: string) => void;
  /** Jump to a camera's recorded footage from a live tile. Omit when the caller lacks
   *  `recordings.view` — that is what hides the button. */
  onOpenRecordings?: (cameraId: string) => void;
  /** Lazy snapshot resolver (cloud) — used for the connecting-phase poster. */
  loadPreview?: (alert: HexAlert) => Promise<string | null>;
}

function AlertTileCard({
  tile,
  alertCount,
  persistent,
  demoteArmed,
  held,
  onStreamingChange,
  PlayerComponent,
  onSelect,
  onExpire,
  onContinue,
  onOpenRecordings,
  loadPreview,
}: AlertTileCardProps) {
  const { cameraId, name, alert } = tile;
  const sev = SEV[alert.severity || 'normal'] || SEV.normal;

  const [phase, setPhase] = useState<StreamPhase>('connecting');
  const [endsAt, setEndsAt] = useState<number | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const persistentRef = useRef(persistent);
  persistentRef.current = persistent;
  const demoteArmedRef = useRef(demoteArmed);
  demoteArmedRef.current = demoteArmed;
  // Held by the board (critical, or the newest lesser alert) → exempt from every countdown.
  const heldRef = useRef(held);
  heldRef.current = held;

  // POSTER: the alert's saved snapshot, shown while the stream is still connecting so the
  // operator sees what tripped the alert without waiting for WebRTC.
  // Sourced like AlertCard: direct snapshotUrl (local) or lazy loadPreview (cloud blob).
  // Once the stream starts (phase leaves 'connecting') the video replaces it.
  //
  // NOT instant any more: a live alert arrives with no image (the node broadcasts before its
  // storage lane has written the event row), so on arrival there is nothing to poster with —
  // the tile goes straight to connecting video, which is the intended behaviour. The effect
  // re-runs on `previewReady` so the poster still appears if the snapshot lands while the
  // stream is slow to connect.
  const [posterUrl, setPosterUrl] = useState<string | null>(alert.snapshotUrl || null);
  useEffect(() => {
    if (alert.snapshotUrl) {
      setPosterUrl(alert.snapshotUrl);
      return;
    }
    setPosterUrl(null);
    if (!loadPreview) return;
    let cancelled = false;
    let made: string | null = null;
    loadPreview(alert)
      .then((url) => {
        if (cancelled) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        if (url) {
          made = url;
          setPosterUrl(url);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (made) URL.revokeObjectURL(made);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert.id, alert.snapshotUrl, loadPreview, alert.previewReady]);

  // Stream started playing → go live. The PRIMARY (most-recent) tile stays live with NO
  // countdown so its subscription keeps the WebRTC transport warm. A non-primary tile
  // previews for PREVIEW_MS then auto-stops — unless the primary isn't streaming yet, in
  // which case it stays live (no countdown) until the demotion effect below arms it, or the
  // board is HOLDING this tile, which never gets a countdown at all (see heldCameraIds).
  const beginPreview = () => {
    if (phaseRef.current !== 'connecting') return;
    if (persistentRef.current || heldRef.current || !demoteArmedRef.current) {
      setPhase('live');
    } else {
      setPhase('preview');
      setEndsAt(Date.now() + PREVIEW_MS);
      setNow(Date.now());
    }
  };

  // Report streaming state to the parent (true from the first frame until unmount). The
  // parent uses this to know when the PRIMARY tile's video is actually visible — the
  // moment demoted siblings may safely start closing.
  const isStreaming = phase !== 'connecting';
  useEffect(() => {
    if (!isStreaming) return;
    onStreamingChange?.(cameraId, true);
    return () => onStreamingChange?.(cameraId, false);
  }, [isStreaming, cameraId, onStreamingChange]);

  // Connect-timeout: drop a tile that never starts streaming — EXCEPT the primary tile,
  // which we keep trying to connect so the warm-up subscription survives. A HELD tile
  // is NOT exempt: with no video there is no stream to hold, and a permanently black tile
  // would sit on a slot the alerts that can be watched need. Its card stays below.
  useEffect(() => {
    const id = setTimeout(() => {
      if (phaseRef.current === 'connecting' && !persistentRef.current) onExpire?.(cameraId);
    }, CONNECT_TIMEOUT_MS);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Promotion / demotion. Demoted from primary (a newer alert arrived) → start the
  // closing countdown — unless the board is holding this tile, which never closes — but
  // ONLY once the new primary is actually streaming (demoteArmed):
  // until then this tile keeps its live stream so there's no all-black gap. Re-promoted
  // to primary (this camera re-alerted to the top) → cancel the countdown and keep it live.
  useEffect(() => {
    if (held && (phaseRef.current === 'preview' || phaseRef.current === 'extended')) {
      // This tile became held mid-countdown — a critical arrived on this camera, or every
      // newer lesser alert was actioned and this is the newest one again. Cancel the close,
      // including a "Continue" extension: 30 minutes is still a close, and a held tile
      // must not be the one that goes dark while the operator is working the alert.
      setPhase('live');
      setEndsAt(null);
    } else if (!persistent && !held && demoteArmed && phaseRef.current === 'live') {
      setPhase('preview');
      setEndsAt(Date.now() + PREVIEW_MS);
      setNow(Date.now());
    } else if (!persistent && !demoteArmed && phaseRef.current === 'preview') {
      // The primary went back to connecting (an even newer alert arrived) mid-countdown:
      // pause the close — this tile is the only live picture until the new one is up.
      setPhase('live');
      setEndsAt(null);
    } else if (persistent && phaseRef.current === 'preview') {
      setPhase('live');
      setEndsAt(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistent, demoteArmed, held]);

  // A fresh alert (alert.id change) re-arms the preview window — but only for a demoted
  // tile already counting down; the primary tile stays live with no timer, and a held one
  // drops its timer entirely.
  useEffect(() => {
    if (heldRef.current) {
      // The new alert leaves this tile held: whatever timer was running (preview, or a
      // "Continue" extension) is dropped — it stays live until it stops being held.
      if (phaseRef.current === 'preview' || phaseRef.current === 'extended') setPhase('live');
      setEndsAt(null);
      return;
    }
    if (!persistentRef.current && phaseRef.current === 'preview') {
      setEndsAt(Date.now() + PREVIEW_MS);
      setNow(Date.now());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert.id]);

  useEffect(() => {
    if (endsAt == null) return;
    const remaining = endsAt - Date.now();
    if (remaining <= 0) {
      onExpire?.(cameraId);
      return;
    }
    const id = setTimeout(() => onExpire?.(cameraId), remaining);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endsAt, cameraId]);

  useEffect(() => {
    if (endsAt == null) return;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [endsAt]);

  const handleContinue = () => {
    setPhase('extended');
    setEndsAt(Date.now() + EXTENDED_MS);
    setNow(Date.now());
    onContinue?.(cameraId);
  };

  const remainingMs = endsAt == null ? 0 : endsAt - now;

  /**
   * WHAT fired, and what KIND of thing it is.
   *
   * The footer used to show one string — `alertName || eventType` — which meant a configured
   * alert ("Perimeter Intrusion — Night") hid the detector behind it, and an operator reading
   * a queue of them couldn't tell an intrusion from a loitering without opening the record.
   * Both are shown now, and the type is dropped only when it would repeat the name, which is
   * exactly the case where the alert was never given a name of its own.
   */
  const alertName = alert.alertName || alert.eventType || 'Alert';
  const alertType = alert.eventType && alert.eventType !== alertName ? alert.eventType : null;
  const severityLabel = (alert.severity || 'normal').replace(/^\w/, (c) => c.toUpperCase());

  // Fullscreen the VIDEO, not the tile: the operator wants the picture bigger, not the
  // severity footer. Held on the wrapper so the player keeps its DOM node — remounting it
  // would tear down this tile's WebRTC subscription and re-handshake mid-alert.
  const videoRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === videoRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      // A tile can vanish on its own — its window elapses, or a newer alert supersedes it —
      // so leaving fullscreen to the operator would strand the browser on a detached element.
      if (document.fullscreenElement === videoRef.current) document.exitFullscreen().catch(() => {});
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    if (document.fullscreenElement === el) document.exitFullscreen().catch(() => {});
    else el.requestFullscreen().catch(() => {});
  }, []);

  // The countdown shows whenever one is running — a demoted tile winding down (preview) and a
  // continued one burning through its extension. "Continue" only on the former: offering it on
  // a tile that has already been continued would be a button that does nothing new.
  const showTimer = endsAt != null;
  const showContinue = phase === 'preview';
  // A held tile carries no countdown, so say WHY it isn't closing — and which of the two
  // reasons applies, since a critical hold ends on an operator action while the newest-alert
  // hold ends on its own the moment something newer arrives.
  const showHeld = held && !showTimer && phase !== 'connecting';
  const heldChip = heldLabel(alert);

  // A light border in the alert's colour, and nothing else tinted — the card body is the
  // panel's own surface, so the video reads against it rather than through a coloured wash.
  return (
    <div className={`group relative overflow-hidden rounded-lg border ${sev.border} bg-card shadow-card transition-shadow hover:shadow-card-hover`}>
      <span
        key={alert.id || alert.timestamp}
        className={`pointer-events-none absolute inset-0 z-20 rounded-lg opacity-0 ring-2 ${sev.ring} animate-modal-pop`}
      />
      {/* Breathing red halo for as long as video is actually running. Now that the LIVE pill
          is gone this is the ONLY thing saying a tile is streaming rather than showing a
          poster — and it says it better, because motion reads from peripheral vision across a
          panel of tiles in a way a word in a corner never did. */}
      {(phase === 'live' || phase === 'extended') && (
        <span className="pointer-events-none absolute inset-0 z-20 animate-live-halo rounded-lg ring-2 ring-red-500/60" />
      )}

      {/* Video — the player draws its own connecting/error overlay. We layer the state /
          close-timer / continue control on TOP and the camera name + time on the bottom.
          A full-bleed transparent button behind the overlays selects the camera; the
          "Continue" control re-enables pointer events so it stays clickable. */}
      <div ref={videoRef} className="relative bg-black">
        <PlayerComponent
          cameraId={cameraId}
          className={isFullscreen ? 'h-full w-full' : 'aspect-video w-full'}
          onStreamStart={beginPreview}
        />

        {/* Connecting-phase poster: the alert snapshot fills the tile the instant it
            mounts, while WebRTC negotiates underneath. Drops away on the first frame. */}
        {phase === 'connecting' && posterUrl && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={posterUrl}
            alt={name}
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            onError={() => setPosterUrl(null)}
          />
        )}

        <button
          type="button"
          aria-label={`Open ${name}`}
          className="absolute inset-0 z-0"
          onClick={() => onSelect?.(cameraId)}
        />

        {/* TOP overlay — WHICH CAMERA on the left, HOW MANY alerts it has on the right, and
            the close timer + continue button between them while this tile is winding down.

            No LIVE / CONNECTING / OFFLINE pill. Every tile in this section is a live stream by
            construction, so a badge saying so was a label on the only thing it could be — and
            with one on every tile it stopped distinguishing anything. The breathing halo around
            a running tile already carries "this picture is moving" from peripheral vision,
            without spending the top-left corner (where the eye lands first) on it. That corner
            now answers the question a wall of tiles actually raises: which camera is this. */}
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-center gap-2 bg-gradient-to-b from-black/70 to-transparent px-2.5 py-1.5">
          {/* Camera name — takes the slack so it truncates rather than pushing the timer or
              the Continue button off the tile when a name is long. */}
          <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-white drop-shadow" title={name}>
            {name}
          </span>

          {/* Shown for BOTH winding-down phases. It used to ride inside the LIVE pill for an
              extended tile, so dropping the pill would have taken "how long have I got" with
              it — and that is the one thing on this overlay an operator acts on. */}
          {showTimer && (
            <span className="shrink-0 rounded bg-black/65 px-1.5 py-0.5 font-mono text-[10px] tabular-nums text-amber-300">
              Closing in {fmtRemaining(remainingMs)}
            </span>
          )}
          {showHeld && (
            <span
              className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold ${
                alert.severity === 'critical' ? 'bg-red-600/85 text-white' : 'bg-black/65 text-emerald-300'
              }`}
              title={heldChip.title}
            >
              {heldChip.chip}
            </span>
          )}
          {showContinue && (
            <button
              type="button"
              onClick={handleContinue}
              className="pointer-events-auto shrink-0 rounded bg-white/90 px-2 py-0.5 text-[10px] font-bold text-slate-900 transition-colors hover:bg-white"
            >
              Continue live stream
            </button>
          )}

          {/* How many alerts this camera currently has outstanding — the tile shows ONE of
              them (the latest), and without this a camera tripping five times looks exactly
              like a camera that tripped once. Last in the row so it holds the corner whether
              or not the transient wind-down controls are on screen. */}
          {alertCount > 0 && (
            <span
              className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums shadow ${sev.badge}`}
              title={`${alertCount} active alert${alertCount === 1 ? '' : 's'} on ${name}`}
            >
              {alertCount} alert{alertCount === 1 ? '' : 's'}
            </span>
          )}
        </div>
        {/* The only thing over the bottom of the picture, and only on hover — same controls,
            same corner, same styling as the camera grid's tiles and the board's click preview.
            Above the full-bleed select button (z-0) so these act instead of selecting. */}
        <div className="absolute bottom-1.5 right-1.5 z-10 flex items-center gap-1.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          {onOpenRecordings && (
            <Tooltip label="Recording playback — review this camera's past footage" side="top">
              <button
                type="button"
                onClick={() => onOpenRecordings(cameraId)}
                className="rounded-lg bg-black/60 p-1.5 text-white transition-colors hover:bg-black/80"
                aria-label="Recording playback"
              >
                <FilmIcon className="h-4 w-4" />
              </button>
            </Tooltip>
          )}
          <Tooltip label={isFullscreen ? 'Exit full screen (Esc)' : 'Full screen'} side="top">
            <button
              type="button"
              onClick={toggleFullscreen}
              className="rounded-lg bg-black/60 p-1.5 text-white transition-colors hover:bg-black/80"
              aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'}
            >
              {isFullscreen ? (
                <ArrowsPointingInIcon className="h-4 w-4" />
              ) : (
                <ArrowsPointingOutIcon className="h-4 w-4" />
              )}
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Footer tab — WHAT tripped, what KIND it is, how bad, and WHEN. The time belongs next
          to the alert name, not over the video: it is the moment of the EVENT, not of the frame
          currently on screen, and pairing the two stops it being misread as a clock on the feed.
          A live tile deliberately has NO Ack button (unlike the static Active-alerts card
          below): it's a transient live preview, acknowledged from its card once the tile's
          window elapses and it settles into the list. */}
      <div className="flex items-center gap-2 border-t border-border bg-card px-2.5 py-1.5">
        <span className="min-w-0 flex-1">
          {/* The alert's own name in the severity colour. It is the line the operator reads
              first, so it is the one that carries how bad this is — which frees the strip
              itself to stay the panel's own white and the colour to mean something by being
              the only coloured thing on it. */}
          <span className={`block truncate text-xs font-semibold ${sev.text}`} title={alertName}>
            {alertName}
          </span>
          {alertType && (
            <span
              className="block truncate text-[10px] font-medium uppercase tracking-wider text-muted-foreground"
              title={alertType}
            >
              {alertType.replace(/[_-]+/g, ' ')}
            </span>
          )}
        </span>
        <span className="shrink-0 text-right">
          <span className={`block text-[11px] font-bold ${sev.text}`}>{severityLabel}</span>
          {alert.timestamp != null && (
            <span
              className="block font-mono text-[10px] tabular-nums text-muted-foreground"
              title={timeAgo(alert.timestamp)}
            >
              {fmtClock(alert.timestamp)}
            </span>
          )}
        </span>
      </div>
    </div>
  );
}

interface SectionAccent {
  /** Icon + label colour — all that separates the two headers now the tinted box is gone. */
  text: string;
}

/**
 * A titled divider for each subsection of the Active-Alerts panel — the streaming tiles and
 * the non-streaming snapshot cards below them.
 *
 * A ruled label rather than the tinted, ringed box it was: two filled boxes stacked down a
 * narrow panel competed with the alert tiles between them, which are the things that are
 * supposed to be shouting. The rule separates without adding another coloured surface, and the
 * count says what it counts ("3 cameras") instead of leaving a bare number in a pill.
 */
function SectionHeader({
  label,
  count,
  countNoun,
  icon: Icon,
  accent,
}: {
  label: string;
  count: number;
  /** Singular noun for the count — "camera" → "3 cameras". */
  countNoun: string;
  icon: typeof PhotoIcon;
  accent: SectionAccent;
}) {
  return (
    <div className="sticky top-0 z-30 -mx-0.5 bg-card/95 px-0.5 py-1.5 backdrop-blur-sm">
      <div className="flex items-center gap-2.5">
        <Icon className={`h-3.5 w-3.5 shrink-0 ${accent.text}`} />
        <h3 className={`shrink-0 text-[11px] font-bold uppercase tracking-[0.08em] ${accent.text}`}>{label}</h3>
        <span className="h-px flex-1 bg-border" />
        <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
          {count} {countNoun}{count === 1 ? '' : 's'}
        </span>
      </div>
    </div>
  );
}

const LIVE_ACCENT: SectionAccent = { text: 'text-emerald-400' };
const PREVIEW_ACCENT: SectionAccent = { text: 'text-muted-foreground' };

export default function AlertTileStack({
  liveTiles,
  persistentCameraId,
  heldCameraIds,
  cards,
  nameById,
  counters,
  PlayerComponent,
  onSelect,
  onExpire,
  onContinue,
  onAcknowledge,
  onViewAlert,
  loadPreview,
  hasMore,
  loadingMore,
  onLoadMore,
  onOpenRecordings,
}: AlertTileStackProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Cameras whose tile currently has visible video (reported by each AlertTileCard).
  // Demotion is armed only while the PRIMARY tile is among them: an older tile keeps its
  // live stream running until the newest alert's video is actually on screen.
  const [streamingIds, setStreamingIds] = useState<Set<string>>(() => new Set());
  const handleStreamingChange = useCallback((id: string, streaming: boolean) => {
    setStreamingIds((prev) => {
      if (streaming === prev.has(id)) return prev;
      const next = new Set(prev);
      if (streaming) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);
  const demoteArmed = !persistentCameraId || streamingIds.has(persistentCameraId);

  // Infinite scroll: load the next page when the sentinel scrolls into the panel's view.
  //
  // Only ever once the list is long enough to scroll. A sentinel in a list shorter than the panel
  // sits inside the root's margin permanently, so it reports "visible" and asks for another page
  // the moment each one lands. On a filtered panel that is a loop with no exit: every page is
  // filtered back out, the list never grows past the fold, and the asking never stops — which
  // walked an untouched map layout through the node's entire alert history, one request per page.
  //
  // "Has the user reached the end of the list" is only a meaningful question when there is a list
  // to reach the end of, so overflow is the condition rather than mere visibility. Reaching past
  // page one to satisfy a filter is a separate, bounded job that useAlertBoard already owns.
  useEffect(() => {
    const root = scrollRef.current;
    const target = sentinelRef.current;
    if (!root || !target || !onLoadMore || cards.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || !hasMore || loadingMore) return;
        if (root.scrollHeight <= root.clientHeight) return;
        onLoadMore();
      },
      { root, rootMargin: '160px' },
    );
    obs.observe(target);
    return () => obs.disconnect();
  }, [hasMore, loadingMore, onLoadMore, cards.length]);

  const nothing = liveTiles.length === 0 && cards.length === 0;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between border-b border-border pb-2.5">
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${liveTiles.length > 0 ? 'bg-red-500 animate-pulse' : 'bg-muted-foreground/40'}`} />
          <h2 className="text-sm font-semibold text-foreground">Active Alerts</h2>
        </div>
        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium tabular-nums text-muted-foreground">
          {liveTiles.length} live
        </span>
      </div>

      <div ref={scrollRef} className="mt-3 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-0.5">
        {nothing && (
          <div className="flex flex-col items-center gap-1.5 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center">
            <span className="text-xs font-medium text-foreground">No active alerts</span>
            <span className="text-[11px] text-muted-foreground">Live streams appear here as alerts arrive.</span>
          </div>
        )}

        {liveTiles.length > 0 && (
          <section className="flex flex-col gap-3">
            <SectionHeader
              label="Live alert camera stream"
              count={liveTiles.length}
              countNoun="camera"
              icon={VideoCameraIcon}
              accent={LIVE_ACCENT}
            />
            {/* Full width below lg (panel spans the screen): 2-up on tablets, 1-up on phones
                and back to a single column beside the canvas on desktop. */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1">
              {liveTiles.map((tile) => (
                <AlertTileCard
                  key={tile.cameraId}
                  tile={tile}
                  alertCount={counters?.[tile.cameraId]?.total ?? 0}
                  persistent={tile.cameraId === persistentCameraId}
                  demoteArmed={demoteArmed}
                  held={!!heldCameraIds?.has(tile.cameraId)}
                  onStreamingChange={handleStreamingChange}
                  PlayerComponent={PlayerComponent}
                  onSelect={onSelect}
                  onExpire={onExpire}
                  onContinue={onContinue}
                  onOpenRecordings={onOpenRecordings}
                  loadPreview={loadPreview}
                />
              ))}
            </div>
          </section>
        )}

        {cards.length > 0 && (
          <section className="flex flex-col gap-3">
            <SectionHeader
              label="Alert preview"
              count={cards.length}
              countNoun="alert"
              icon={PhotoIcon}
              accent={PREVIEW_ACCENT}
            />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1">
              {cards.map((a) => (
                <AlertCard
                  key={a.id || `${a.cameraId}-${a.timestamp}`}
                  alert={a}
                  name={nameById[a.cameraId] || a.cameraId}
                  onSelect={onSelect}
                  onAcknowledge={onAcknowledge}
                  onViewAlert={onViewAlert}
                  loadPreview={loadPreview}
                />
              ))}
            </div>
          </section>
        )}

        {/* Infinite-scroll sentinel + loading skeleton */}
        <div ref={sentinelRef} className="h-1 w-full shrink-0" />
        {loadingMore && (
          <div className="flex items-center justify-center gap-2 py-2 text-[11px] text-muted-foreground">
            <BoltIcon className="h-3.5 w-3.5 animate-pulse" /> Loading more…
          </div>
        )}
      </div>
    </div>
  );
}
